import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  AccountCircle,
  PersonOutline,
  BusinessCenter,
  LockOutlined,
} from "@mui/icons-material";
import { signupWithAuth0, loginWithGoogle } from '../../services/apiService';
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { MuiTelInput } from 'mui-tel-input';

export function SignupForm() {
  const [formData, setFormData] = useState({
    signupFullName: "",
    signupEmail: "",
    signupUsername: "",
    signupMobile: "",
    signupPassword: "",
    signupAccountType: "individual",
    signupCompanyName: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();


  const handlePasswordVisibility = () => setShowPassword((prev) => !prev);

  const validateFullName = (fullName) => {
    if (!fullName || !fullName.trim()) return "Full name is required";
    if (fullName.trim().length < 3) return "Please enter a valid full name";
    return "";
  };

  const validateEmail = (email) => {
    if (!email) return "Email is required";
    if (!/\S+@\S+\.\S+/.test(email)) return "Invalid email format";
    return "";
  };

  const validateUsername = (username) => {
    if (!username) return "Username is required";
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile) return "Mobile number is required";
    const cleanNumber = mobile.replace(/\D/g, '');
    if (cleanNumber.length < 7) return "Invalid mobile number format";
    return "";
  };

  const validateCompanyName = (companyName, accountType) => {
    const trimmedCompany = (companyName || "").trim();
    if (accountType === "business" && !trimmedCompany) {
      return "Company name is required for business accounts";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Password must be at least 6 characters";
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
    return "";
  };

  const validateSignup = () => {
    const newErrors = {};
    newErrors.signupFullName = validateFullName(formData.signupFullName);
    newErrors.signupEmail = validateEmail(formData.signupEmail);
    newErrors.signupUsername = validateUsername(formData.signupUsername);
    newErrors.signupMobile = validateMobile(formData.signupMobile);
    newErrors.signupPassword = validatePassword(formData.signupPassword);
    const companyError = validateCompanyName(formData.signupCompanyName, formData.signupAccountType);
    if (companyError) {
      newErrors.signupCompanyName = companyError;
    }

    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (!validateSignup()) return;

  try {
    const trimmedFullName = formData.signupFullName.trim();
    const trimmedCompanyName = formData.signupCompanyName.trim();

    await signupWithAuth0(
      formData.signupEmail,
      formData.signupPassword,
      formData.signupUsername,
      trimmedFullName,
      formData.signupMobile,
      formData.signupAccountType,
      trimmedCompanyName
    );

      toast.success("Account created successfully! Please log in to continue.", { autoClose: 3000 });
      navigate("/login");

    } catch (error) {
      console.log("Signup error:", error.response?.data);

      const errorResponse = error.response?.data;
      const errorCode = errorResponse?.error || errorResponse?.code;
      const errorDescription = errorResponse?.error_description || errorResponse?.description || errorResponse?.message;
      const errorMessage = errorDescription || error.message || "An error occurred during signup";

      const isDuplicateEmail = 
        errorCode === "user_exists" ||
        errorMessage?.toLowerCase().includes("already exists") ||
        errorMessage?.toLowerCase().includes("user exists") ||
        errorMessage?.toLowerCase().includes("email already") ||
        errorMessage?.toLowerCase().includes("already registered") ||
        errorMessage?.toLowerCase().includes("duplicate") ||
        (errorCode === "invalid_signup" && errorMessage?.toLowerCase().includes("email")) ||
        (errorResponse?.code === "invalid_signup" && errorResponse?.data?.identifierType === "email");

      // Handle duplicate email/user exists errors first (most common case)
      if (isDuplicateEmail) {
        setErrors(prevErrors => ({
          ...prevErrors,
          signupEmail: "This email is already registered. Please use a different email or try logging in."
        }));
        toast.error("This email is already registered. Please use a different email or try logging in.", { autoClose: 3000 });
      }
      else if (errorResponse?.code === "invalid_signup") {
        const errorData = errorResponse;
        const backendErrors = {};

        if (errorData.data?.identifierType === "username") {
          backendErrors.signupUsername = errorData.description || "Invalid username";
        }
        else if (errorData.data?.identifierType === "email") {
          backendErrors.signupEmail = errorData.description || "Invalid email";
        }
        else {
          toast.error("Signup failed: " + errorData.description, { autoClose: 3000 });
          return;
        }

        setErrors(prevErrors => ({
          ...prevErrors,
          ...backendErrors
        }));

        toast.error("Please fix the validation errors and try again", { autoClose: 3000 });
      } 
      else {
        const displayMessage = errorMessage || "An error occurred during signup";
        toast.error("Signup failed: " + displayMessage, { autoClose: 3000 });
      }
    }
  };


  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "signupAccountType") {
      setFormData((prev) => ({
        ...prev,
        signupAccountType: value,
        signupCompanyName: value === "business" ? prev.signupCompanyName : "",
      }));

      setErrors((prev) => {
        const updatedErrors = { ...prev };
        if (value === "business") {
          const companyError = validateCompanyName(formData.signupCompanyName, value);
          if (companyError) {
            updatedErrors.signupCompanyName = companyError;
          } else {
            delete updatedErrors.signupCompanyName;
          }
        } else {
          delete updatedErrors.signupCompanyName;
        }
        return updatedErrors;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    let fieldError = "";
    switch (name) {
      case "signupFullName":
        fieldError = validateFullName(value);
        break;
      case "signupEmail":
        fieldError = validateEmail(value);
        break;
      case "signupUsername":
        fieldError = validateUsername(value);
        break;
      case "signupMobile":
        fieldError = validateMobile(value);
        break;
      case "signupCompanyName":
        fieldError = validateCompanyName(value, formData.signupAccountType);
        break;
      case "signupPassword":
        fieldError = validatePassword(value);
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: fieldError || undefined,
    }));

    if (!fieldError) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };


  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, signupMobile: value }));

    const fieldError = validateMobile(value);


    setErrors((prev) => ({
      ...prev,
      signupMobile: fieldError || undefined,
    }));


    if (!fieldError) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.signupMobile;
        return newErrors;
      });
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const response = await loginWithGoogle();

      if (!response || !response.token) {
        throw new Error("No token received from Google sign-up.");
      }

      Cookies.set("authToken", response.token, { expires: 1, secure: true });
      Cookies.set("userId", response.user.user_id, { expires: 1, secure: true });
      Cookies.set('Org_id', response.user.primary_org_id, { expires: 1, secure: true });
      Cookies.set('Role_id', response.user.role_id, { expires: 1, secure: true });
      Cookies.set("username", response.user.name || response.user.username || response.user.nickname || response.user.email || 'Guest', { expires: 1, secure: true });
      Cookies.set("userEmail", response.user.email || '', { expires: 1, secure: true });

      console.log("🔹 Token stored successfully");
      console.log("🔹 Redirecting to dashboard after token storage");
      toast.success("Signup successful!", { autoClose: 2000 });
      setTimeout(() => {
        navigate("/userdashboard");
      }, 2000);
    } catch (error) {
      alert("Google Sign-up/Login failed: " + error.message);
    }
  };


  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box', minWidth: 0 }}>
      <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Typography
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 700,
            fontSize: { xs: '1.4375rem', sm: '1.4375rem', md: '1.4375rem' },
            textAlign: 'center',
            mb: { xs: 0.75, sm: 0.875, md: 0.875 },
            color: '#383838',
            lineHeight: '100%',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            px: { xs: 0.5, sm: 0 },
            display: 'block',
            margin: 0,
          }}
        >
          Create Account
        </Typography>
      </Box>

      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontWeight: 400,
          fontSize: { xs: '0.84375rem', sm: '0.84375rem', md: '0.84375rem' },
          textAlign: 'center',
          mb: { xs: 1.75, sm: 2, md: 2 },
          color: '#4A5565',
          lineHeight: '1.5rem',
          letterSpacing: '-0.31px',
          px: { xs: 1, sm: 0 },
        }}
      >
        Welcome! Create your account to get started.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSignup}
        sx={{ mt: { xs: 0.5, sm: 0.5, md: 0.5 } }}
      >
        <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              color: '#383838',
              lineHeight: '100%',
              mb: { xs: 0.5, sm: 0.625, md: 0.5 },
            }}
          >
            Full Name
          </Typography>
          <TextField
            fullWidth
            name="signupFullName"
            placeholder="John Doe"
            value={formData.signupFullName}
            onChange={handleChange}
            error={!!errors.signupFullName}
            helperText={errors.signupFullName}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start"
                  sx={{
                    minWidth: { xs: '40px', sm: '48px', md: '48px' },
                    mr: { xs: 0.5, sm: 0.5, md: 0.5 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <PersonOutline
                    sx={{
                      color: '#9CA3AF',
                      fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                py: { xs: 1.25, sm: 1.375, md: 1.25 },
                px: { xs: 1.75, sm: 2, md: 2 },
                color: '#0A0A0A',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#0A0A0A80',
                opacity: 1,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiFormHelperText-root': {
                color: '#EF4444',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                mt: 0.5,
              },
            }}
          />
        </Box>

        <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              color: '#383838',
              lineHeight: '100%',
              mb: { xs: 0.5, sm: 0.625, md: 0.5 },
            }}
          >
            User Name
          </Typography>
          <TextField
            fullWidth
            name="signupUsername"
            placeholder="username"
            value={formData.signupUsername}
            onChange={handleChange}
            error={!!errors.signupUsername}
            helperText={errors.signupUsername}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start"
                  sx={{
                    minWidth: { xs: '40px', sm: '48px', md: '48px' },
                    mr: { xs: 0.5, sm: 0.5, md: 0.5 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <AccountCircle
                    sx={{
                      color: '#9CA3AF',
                      fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                py: { xs: 1.25, sm: 1.375, md: 1.25 },
                px: { xs: 1.75, sm: 2, md: 2 },
                color: '#0A0A0A',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#0A0A0A80',
                opacity: 1,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiFormHelperText-root': {
                color: '#EF4444',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                mt: 0.5,
              },
            }}
          />
        </Box>

        <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              color: '#383838',
              lineHeight: '100%',
              mb: { xs: 0.5, sm: 0.625, md: 0.5 },
            }}
          >
            Email
          </Typography>
          <TextField
            fullWidth
            name="signupEmail"
            placeholder="you@example.com"
            value={formData.signupEmail}
            onChange={handleChange}
            error={!!errors.signupEmail}
            helperText={errors.signupEmail}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start"
                  sx={{
                    minWidth: { xs: '40px', sm: '48px', md: '48px' },
                    mr: { xs: 0.5, sm: 0.5, md: 0.5 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <EmailOutlined
                    sx={{
                      color: '#9CA3AF',
                      fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                py: { xs: 1.25, sm: 1.375, md: 1.25 },
                px: { xs: 1.75, sm: 2, md: 2 },
                color: '#0A0A0A',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#0A0A0A80',
                opacity: 1,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiFormHelperText-root': {
                color: '#EF4444',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                mt: 0.5,
              },
            }}
          />
        </Box>

        <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
          <FormControl
            component="fieldset"
            fullWidth
            sx={{
              '& .MuiFormLabel-root': {
                fontFamily: '"IBM Plex Sans", sans-serif',
                color: '#383838',
                fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                fontWeight: 500,
                mb: { xs: 0.5, sm: 0.625, md: 0.5 },
                '&.Mui-focused': {
                  color: '#383838',
                },
              },
              '& .MuiFormControlLabel-label': {
                fontFamily: '"Inter", sans-serif',
                color: '#383838',
                fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              },
            }}
          >
            <FormLabel component="legend">Account type</FormLabel>
            <RadioGroup
              row
              aria-label="Account type"
              name="signupAccountType"
              value={formData.signupAccountType}
              onChange={handleChange}
              sx={{
                '& .MuiFormControlLabel-root': {
                  marginRight: { xs: 2, sm: 3 },
                },
                '& .MuiRadio-root': {
                  color: '#9CA3AF',
                  '&.Mui-checked': {
                    color: '#0056D2',
                  },
                },
              }}
            >
              <FormControlLabel value="individual" control={<Radio />} label="Individual" />
              <FormControlLabel value="business" control={<Radio />} label="Business" />
            </RadioGroup>
          </FormControl>
        </Box>

        {formData.signupAccountType === "business" && (
          <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                fontWeight: 500,
                fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                color: '#383838',
                lineHeight: '100%',
                mb: { xs: 0.5, sm: 0.625, md: 0.5 },
              }}
            >
              Company Name
            </Typography>
            <TextField
              fullWidth
              name="signupCompanyName"
              placeholder="Company Name"
              value={formData.signupCompanyName}
              onChange={handleChange}
              error={!!errors.signupCompanyName}
              helperText={errors.signupCompanyName}
              InputProps={{
                startAdornment: (
                  <InputAdornment 
                    position="start"
                    sx={{
                      minWidth: { xs: '40px', sm: '48px', md: '48px' },
                      mr: { xs: 0.5, sm: 0.5, md: 0.5 },
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <BusinessCenter
                      sx={{
                        color: '#9CA3AF',
                        fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
                '& .MuiInputBase-input': {
                  py: { xs: 1.25, sm: 1.375, md: 1.25 },
                  px: { xs: 1.75, sm: 2, md: 2 },
                  color: '#0A0A0A',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                  lineHeight: '100%',
                  letterSpacing: '-0.31px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#0A0A0A80',
                  opacity: 1,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                  lineHeight: '100%',
                  letterSpacing: '-0.31px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#EF4444',
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                  mt: 0.5,
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              color: '#383838',
              lineHeight: '100%',
              mb: { xs: 0.5, sm: 0.625, md: 0.5 },
            }}
          >
            Mobile number
          </Typography>
          <MuiTelInput
            fullWidth
            value={formData.signupMobile}
            onChange={handlePhoneChange}
            error={!!errors.signupMobile}
            helperText={errors.signupMobile}
            defaultCountry="US"
            placeholder="Mobile number"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  width: 'auto',
                  minWidth: 200,
                },
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                py: { xs: 1.25, sm: 1.375, md: 1.25 },
                px: { xs: 1.75, sm: 2, md: 2 },
                color: '#0A0A0A',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#0A0A0A80',
                opacity: 1,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiFormHelperText-root': {
                color: '#EF4444',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                mt: 0.5,
              },
              '& .MuiTelInput-Flag': {
                marginRight: '8px',
              },
              '& .MuiTelInput-IconButton': {
                padding: '4px',
                color: '#9CA3AF',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ mb: { xs: 1.5, sm: 2, md: 1.5 } }}>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              color: '#383838',
              lineHeight: '100%',
              mb: { xs: 0.5, sm: 0.625, md: 0.5 },
            }}
          >
            Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            name="signupPassword"
            placeholder="Enter your password"
            value={formData.signupPassword}
            onChange={handleChange}
            error={!!errors.signupPassword}
            helperText={errors.signupPassword}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start"
                  sx={{
                    minWidth: { xs: '40px', sm: '48px', md: '48px' },
                    mr: { xs: 0.5, sm: 1, md: 1 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <LockOutlined
                    sx={{
                      color: '#9CA3AF',
                      fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handlePasswordVisibility}
                    edge="end"
                    size="small"
                    sx={{
                      color: '#9CA3AF',
                      p: { xs: 0.5, sm: 1, md: 1 },
                      '&:hover': {
                        color: '#6B7280',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOff
                        sx={{
                          fontSize: { xs: '1.0625rem', sm: '1.1875rem', md: '1.1875rem' },
                        }}
                      />
                    ) : (
                      <Visibility
                        sx={{
                          fontSize: { xs: '1.0625rem', sm: '1.1875rem', md: '1.1875rem' },
                        }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: '#E5E7EB',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0056D2',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                py: { xs: 1.25, sm: 1.375, md: 1.25 },
                px: { xs: 1.75, sm: 2, md: 2 },
                color: '#0A0A0A',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#0A0A0A80',
                opacity: 1,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '100%',
                letterSpacing: '-0.31px',
              },
              '& .MuiFormHelperText-root': {
                color: '#EF4444',
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                mt: 0.5,
              },
            }}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            height: { xs: '44px', sm: '44px', md: '46px' },
            borderRadius: '4px',
            fontWeight: 600,
            fontSize: { xs: '0.90625rem', sm: '0.84375rem', md: '0.84375rem' },
            mb: { xs: 2, sm: 2, md: 2 },
            background: '#0F172A',
            color: '#ffffff',
            textTransform: 'none',
            boxShadow: '0px 2px 4px rgba(15, 23, 42, 0.2)',
            '&:hover': {
              background: '#1E293B',
              boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.3)',
            },
            '&:active': {
              background: '#0A0F1A',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Sign Up
        </Button>

        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2, md: 2 } }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: '#4A5565',
              fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
              lineHeight: '24px',
              fontWeight: 400,
              letterSpacing: '-0.31px',
              textAlign: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.25, sm: 0.5, md: 0.5 },
            }}
          >
            Already have an account?{' '}
            <Link
              onClick={() => navigate('/login')}
              sx={{
                fontFamily: '"IBM Plex Sans", sans-serif',
                cursor: 'pointer',
                color: '#3D9764',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                lineHeight: '24px',
                letterSpacing: '-0.31px',
                textAlign: 'center',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#2d7a4f',
                },
                transition: 'color 0.2s ease',
              }}
            >
              Login
            </Link>
          </Typography>
        </Box>

        <Divider
          sx={{
            mb: { xs: 2, sm: 2, md: 2 },
            color: '#0F172A',
            fontSize: { xs: '0.84375rem', sm: '0.84375rem', md: '0.84375rem' },
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 500,
            letterSpacing: '0.1px',
            lineHeight: '100%',
            '&::before, &::after': {
              borderColor: '#E5E7EB',
            },
          }}
        >
          or Sign in with
        </Divider>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleGoogleSignup}
          sx={{
            fontFamily: '"Inter", sans-serif',
            height: { xs: '44px', sm: '44px', md: '46px' },
            borderRadius: { xs: '4px', sm: '4px', md: '8px' },
            fontSize: { xs: '0.78125rem', sm: '0.84375rem', md: '0.84375rem' },
            fontWeight: 500,
            color: '#383838',
            borderColor: '#E5E7EB',
            backgroundColor: '#FFFFFF',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#F9FAFB',
              borderColor: '#D1D5DB',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 28 28"
            fill="none"
            sx={{
              width: { xs: '18px', sm: '20px', md: '20px' },
              height: { xs: '18px', sm: '20px', md: '20px' },
              mr: 1,
            }}
          >
            <path
              d="M27.5007 14.0819C27.5007 12.9493 27.4088 12.1227 27.2099 11.2656H14.2764V16.3777H21.868C21.715 17.6482 20.8885 19.5614 19.0518 20.847L19.026 21.0182L23.1154 24.1861L23.3987 24.2144C26.0006 21.8114 27.5007 18.2757 27.5007 14.0819Z"
              fill="#4285F4"
            />
            <path
              d="M14.2764 27.5508C17.9957 27.5508 21.1181 26.3263 23.3987 24.2142L19.0518 20.8468C17.8886 21.658 16.3274 22.2243 14.2764 22.2243C10.6337 22.2243 7.54189 19.8214 6.43976 16.5L6.27821 16.5137L2.02607 19.8045L1.97046 19.9591C4.23571 24.459 8.88871 27.5508 14.2764 27.5508Z"
              fill="#34A853"
            />
            <path
              d="M6.43936 16.5005C6.14855 15.6434 5.98025 14.725 5.98025 13.776C5.98025 12.827 6.14855 11.9087 6.42406 11.0516L6.41636 10.869L2.11093 7.52539L1.97006 7.59239C1.03644 9.45974 0.500732 11.5567 0.500732 13.776C0.500732 15.9954 1.03644 18.0922 1.97006 19.9596L6.43936 16.5005Z"
              fill="#FBBC05"
            />
            <path
              d="M14.2764 5.32642C16.8631 5.32642 18.6079 6.44374 19.6029 7.37746L23.4905 3.58158C21.1029 1.36223 17.9957 0 14.2764 0C8.88871 0 4.23571 3.09177 1.97046 7.59167L6.42446 11.0508C7.54189 7.72947 10.6337 5.32642 14.2764 5.32642Z"
              fill="#EB4335"
            />
          </Box>
          Sign in with Google
        </Button>
      </Box>
    </Box>
  );
}

export default SignupForm;